import React, { useCallback, useEffect, useRef, useState } from 'react';
import { socket } from '../chat/useSocket';
import { singleCheck, doubleCheck, arrowLIcon, fileSelect, sendIcon } from '../../static/svgs';
import ProfileSectionCard from '../shared/ProfileSectionCard';
import SupportIcon from './SupportIcon';
import { supportRequest } from './supportApi';
import SupportAttachment from './SupportAttachment';
import useSupportUnread from './useSupportUnread';
import packageInfo from '../../../package.json';
import '../tasks/task.css';
import '../chat/style.css';
import '../shared/companyPicker.css';
import '../profile/profile.css';
import './support.css';

const sections = { start: 'Начало работы', home: 'Главная', documents: 'Документы', tasks: 'Задачи', chat: 'Чат', profile: 'Профиль' };
const titles = { hub: 'Помощь и поддержка', about: 'О программе', version: 'Версия программы', messages: 'Чат с техподдержкой', suggestions: 'Предложения по улучшению сервиса', ...sections };
const merge = (old, incoming) => Array.from(new Map([...old, ...incoming].map(m => [m.id, m])).values());
export default function Support({ onBack }) {
  const [page, setPage] = useState('hub');
  const [rows, setRows] = useState([]);
  const [articles, setArticles] = useState([]);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [more, setMore] = useState(false);
  const list = useRef(null);
  const generation = useRef(0);
  const fileInput = useRef(null);
  const rowCache = useRef([]);
  const unread = useSupportUnread();
  const load = useCallback(async (before) => {
    const current = generation.current;
    try {
      if (sections[page]) {
        const data = await supportRequest('articles');
        if (current === generation.current) { setArticles(data.articles); setError(''); }
        return;
      }
      if (!['messages', 'suggestions'].includes(page)) return;
      const data = await supportRequest(`${page}${before ? `?before=${before}` : ''}`);
      if (current !== generation.current) return;
      const incoming = data[page];
      const old = rowCache.current;
      const reset = !before && incoming.length > 0 && old.length > 0 && !incoming.some(m => old.some(r => r.id === m.id));
      rowCache.current = merge(reset ? [] : old, incoming).sort((a, b) => page === 'messages' ? a.id - b.id : b.id - a.id);
      setRows(rowCache.current);
      if (reset || before || !list.current?.dataset.loaded) setMore(data.has_more);
      if (list.current) list.current.dataset.loaded = 'true';
      if (page === 'messages' && incoming.length && document.visibilityState === 'visible' && document.hasFocus()) {
        await supportRequest('read', { method: 'POST', body: JSON.stringify({ through_id: Math.max(...incoming.map(m => m.id)) }) });
      }
      setError('');
    } catch (e) { if (current === generation.current) setError(e.message); }
  }, [page]);
  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    rowCache.current = [];
    setRows([]); setMore(false); setText(''); setFiles([]); setError('');
    if (list.current) delete list.current.dataset.loaded;
    setLoading(true);
    load()
      .catch(e => { if (current === generation.current) setError(e.message); })
      .finally(() => { if (current === generation.current) setLoading(false); });
    const refresh = () => load();
    socket.on('supportChanged', refresh);
    socket.on('connect', refresh);
    window.addEventListener('focus', refresh);
    const timer = setInterval(refresh, 10000);
    return () => { generation.current += 1; socket.off('supportChanged', refresh); socket.off('connect', refresh); window.removeEventListener('focus', refresh); clearInterval(timer); };
  }, [load]);
  async function send(e) {
    e.preventDefault();
    if (busy || (!text.trim() && !files.length)) return;
    setBusy(true); setError('');
    try {
      if (page === 'suggestions') await supportRequest(page, { method: 'POST', body: JSON.stringify({ text }) });
      else {
        const body = new FormData(); body.append('text', text);
        files.forEach(file => body.append('files[]', file, file.name));
        await supportRequest('messages', { method: 'POST', body });
      }
      setText(''); setFiles([]); if (fileInput.current) fileInput.current.value = '';
      await load();
      if (page === 'messages') requestAnimationFrame(() => list.current?.scrollTo(0, list.current.scrollHeight));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  function back() {
    if (busy) return;
    if (page === 'hub') onBack();
    else setPage(page === 'version' || sections[page] ? 'about' : 'hub');
  }
  const links = page === 'hub' ? ['about', 'messages', 'suggestions'] : page === 'about' ? ['version', ...Object.keys(sections)] : [];
  const isChat = page === 'messages';
  const errorNotice = error && <div role="alert" className="support-error manrope-bold">{error}<button type="button" className="support-text-button manrope-bold" onClick={() => load()}>Повторить</button></div>;
  const loadMore = <button type="button" className="support-more manrope-bold" onClick={() => load(Math.min(...rows.map(m => m.id)))}>{isChat ? 'Загрузить предыдущие' : 'Загрузить ещё'}</button>;
  return <div className={`main_cntr_chat section-page support-page ${isChat ? 'chat-page support-chat-page' : 'profile-page profile-page--with-heading'}`}>
    <div className="section-page-heading manrope-bold color_white">Помощь и поддержка</div>
    <div className="chat_page_header section-page_title_cntr">
      <button type="button" className="page-header-nav-back" onClick={back} disabled={busy} aria-label="Назад">{arrowLIcon}</button>
      <div className="chat_page_header_titles">
        <div className="manrope-bold color_white section-company-title">{titles[page]}</div>
        {isChat && <div className="chat_page_chat_title color_white">Личная переписка с поддержкой</div>}
      </div>
    </div>
    {isChat ? <div className="cntr_messages support-chat-panel">
      {errorNotice}
      <div className="messages support-history" ref={list} aria-live="polite">
        {more && loadMore}
        {loading && <div className="profile-empty manrope-bold">Загрузка…</div>}
        {!loading && !rows.length && <div className="support-empty manrope-bold"><SupportIcon section="messages" /><div>Напишите в техподдержку</div><p className="manrope-400">Опишите вопрос и при необходимости приложите файлы.</p></div>}
        {rows.map(m => {
          const own = m.sender_type === 'subscriber';
          return <div key={m.id} className={`row_message support-message-row ${own ? 'support-message-row--own' : ''}`}>
            <div className={`${own ? 'cntr_message_item_my' : 'cntr_message_item'} support-bubble`}>
              <div className={`chat_msg_sender_header ${own ? 'chat_msg_sender_header--company' : ''}`}>
                {!own && <div className="chat_msg_sender_avatar chat_msg_sender_avatar--initials">ТП</div>}
                <span className="user_name manrope-bold">{own ? 'Вы' : 'Техподдержка'}</span>
              </div>
              {m.has_file && <SupportAttachment message={m} />}
              <div className="message_text manrope-bold">{m.text}</div>
              <div className="support-message-meta">
                <time className="datetime manrope-400 color_grey" dateTime={m.datetime}>{new Date(m.datetime).toLocaleString('ru-RU')}</time>
                {own && <span className="support-message-check" title={m.status === 'viewed' ? 'Прочитано' : m.status === 'received' ? 'Доставлено' : 'Отправлено'}>{m.status === 'viewed' ? doubleCheck('#3B82F6', 18) : m.status === 'received' ? doubleCheck('#696868', 18) : singleCheck('#696868', 16)}</span>}
              </div>
            </div>
          </div>;
        })}
      </div>
      {!!files.length && <div className="support-selected-files">{files.map((file, index) => <div className="support-file-chip manrope-400" key={`${file.name}-${index}`}><span>{file.name}</span><button type="button" disabled={busy} aria-label={`Убрать ${file.name}`} onClick={() => { setFiles(old => old.filter((_, i) => i !== index)); if (fileInput.current) fileInput.current.value = ''; }}>×</button></div>)}</div>}
      <form className="cntr_input_message support-composer" onSubmit={send}>
        <textarea className="input_message nores manrope-400" aria-label="Сообщение" placeholder="Сообщение..." value={text} onChange={e => setText(e.target.value)} maxLength={10000} disabled={busy} />
        <div className="cntr_input_btns">
          <div className="file_upload"><button type="button" className="btn_send support-attach" aria-label="Прикрепить файлы" title="Прикрепить файлы" disabled={busy} onClick={() => fileInput.current?.click()}>{fileSelect}</button><input ref={fileInput} type="file" multiple disabled={busy} onChange={e => setFiles(Array.from(e.target.files))} /></div>
          <button type="submit" className="btn_send" aria-label={busy ? 'Отправка…' : 'Отправить'} title="Отправить" disabled={busy || (!text.trim() && !files.length)}>{sendIcon}</button>
        </div>
      </form>
    </div> : <div className="tasks_subcntr section-page-picker profile-body support-body">
      {errorNotice}
      {!!links.length && <div className="company-picker-list company-picker-panel profile-hub-list">{links.map(key => <ProfileSectionCard key={key} title={titles[key]} icon={<SupportIcon section={key} />} onClick={() => setPage(key)} badge={key === 'messages' ? unread : 0} />)}</div>}
      {page === 'version' && <div className="support-content-card"><div className="support-content-title manrope-bold"><SupportIcon section="version" />Версия программы</div><p className="support-version manrope-bold">Версия программы: {packageInfo.version}</p></div>}
      {sections[page] && <div className="support-content-card"><div className="support-content-title manrope-bold"><SupportIcon section={page} />{titles[page]}</div><div className="support-article manrope-400">{articles.find(a => a.slug === page)?.text || (loading ? 'Загрузка…' : 'Инструкция пока не заполнена.')}</div></div>}
      {page === 'suggestions' && <>
        <form className="support-content-card support-suggestion-form" onSubmit={send}>
          <label className="profile-field manrope-bold" htmlFor="support-suggestion">Ваше предложение<textarea id="support-suggestion" className="input_task manrope-400 support-suggestion-input" aria-label="Предложение" placeholder="Расскажите, что можно улучшить в сервисе" value={text} onChange={e => setText(e.target.value)} maxLength={10000} disabled={busy} /></label>
          <button type="submit" className="profile-save-btn manrope-bold" disabled={busy || !text.trim()}>{busy ? 'Отправка…' : 'Отправить'}</button>
        </form>
        <div className="support-suggestions-history" ref={list} aria-live="polite">
          <h3 className="support-history-title manrope-bold">Ранее отправленные предложения</h3>
          {loading && <div className="profile-empty manrope-bold">Загрузка…</div>}
          {!loading && !rows.length && <div className="profile-empty manrope-400">Вы пока не отправляли предложений.</div>}
          {rows.map(m => <article key={m.id} className="support-content-card"><p className="support-text manrope-400">{m.text}</p><time className="support-suggestion-date manrope-400" dateTime={m.datetime}>{new Date(m.datetime).toLocaleString('ru-RU')}</time></article>)}
          {more && loadMore}
        </div>
      </>}
    </div>}
  </div>;
}
